import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Calculation from 'classes/Recipe/Detail/Calculation/Calculation';
import { WithSelectedCulture } from 'components/desktop/Hocs/WithSelectedCulture';
import { HighlightingSegment } from 'components/desktop/Recipe/RecipeTabs/Components/HighlightingSegment/HighlightingSegment';
import { ToolTipCell } from 'components/desktop/Recipe/RecipeTabs/Components/IngredientTooltip/IngredientTooltip';
import LinkCell from 'components/desktop/Recipe/RecipeTabs/Components/LinkCell/LinkCell';
import {
	ingredientAmountKey,
	ingredientNameKey,
	ingredientUnitKey,
} from 'components/desktop/Recipe/RecipeTabs/Components/NutrientFilterSearchComponentWrapper';
import {
	Translation,
	calculateSegmentLengths,
	enrichIngredients,
	flattenSegments,
	mapToTableData,
} from 'components/desktop/Recipe/RecipeTabs/recipeComputations';
import { AirTable } from 'components/desktop/_general/AirTable/AirTable';
import PERMISSIONS from 'enums/permissions';
import { arePermissionsInUserPermissions } from 'functions/tokenFunctions';
import { RefreshType } from 'hooks/useRefresh';
import { IncorporationShareType } from 'types/Recipe/IngredientIncorporationShare';
import { Segment } from 'types/Recipe/Segment';

import './DefaultView.scss';

export const createDefaultColumns = (t: Translation) => {
	const defaultColumns = [
		{ columnName: t('_general:AMOUNT'), key: ingredientAmountKey, numeric: true, sticky: true },
		{ columnName: t('_general:UNIT'), key: ingredientUnitKey, numeric: false, sticky: true },
		{
			columnName: t('_general:AMOUNT_BEFORE'),
			key: 'additionBefore',
			numeric: false,
			sticky: true,
		},
		{ columnName: t('_general:INGREDIENT'), key: ingredientNameKey, sticky: true },
		{
			columnName: t('_general:AMOUNT_AFTER'),
			key: 'additionAfter',
			numeric: false,
			sticky: true,
		},
	];

	return defaultColumns;
};

interface Props {
	steps: Segment[];
	/**
	 * zero‐based index of the active step.
	 * any step < activeIndex will show as “completed”,
	 * step === activeIndex will be highlighted,
	 * steps > activeIndex stay in default state.
	 */
}

export const VerticalStepper: React.FC<Props> = ({ steps }) => {
	const activeIndex = 0;
	return (
		<div className="vertical-stepper">
			{steps.map((step, i) => {
				const isActive = i === activeIndex;
				const isCompleted = i < activeIndex;
				return (
					<div key={i} className={`step active completed`}>
						<div className="circle">{isCompleted ? '✓' : i + 1}</div>
						<div className="content">
							{step.instruction && (
								<div className="description">{step.instruction}</div>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

interface IProps {
	refresh: RefreshType;
	selectedCultureCode: string;
	readOnly: boolean;
	calculation: Calculation | null;
	quantityMethod: string;
	segments: Segment[];
	incorporationShares: IncorporationShareType[];
}

const DefaultView = (props: IProps) => {
	const { t } = useTranslation();
	const flatIngredients = flattenSegments(props.segments);

	const [element, setElement] = useState<JSX.Element>(<></>);

	const joinedIngredients = enrichIngredients(
		props.calculation,
		props.incorporationShares,
		flatIngredients
	);

	function useMediaQuery(query: string) {
		const [matches, setMatches] = useState(() =>
			typeof window !== 'undefined' ? window.matchMedia(query).matches : false
		);

		useEffect(() => {
			const media = window.matchMedia(query);
			if (media.matches !== matches) {
				setMatches(media.matches);
			}
			const listener = () => setMatches(media.matches);
			media.addEventListener('change', listener);
			return () => media.removeEventListener('change', listener);
		}, [query, matches]);

		return matches;
	}

	const segmentLengths = calculateSegmentLengths(props.segments);
	const isDesktop = useMediaQuery('(min-width:1024px)');

	const tableData = joinedIngredients.map((ingredient) => ({
		...mapToTableData(ingredient, [], props.selectedCultureCode, props.quantityMethod),
		customCell: {
			ingredientName: (
				<ToolTipCell
					refresh={props.refresh}
					ingredient={ingredient.ingredient}
					setElement={setElement}
					lastCell={segmentLengths.includes(ingredient.index + 1)}
				/>
			),
			ingredientUnit: (
				<LinkCell
					link={
						ingredient.ingredient.unitId
							? `/unit/management?id=${ingredient.ingredient.unitId}`
							: '/unit/management'
					}
					title={ingredient.ingredient.unit}
					id={ingredient.ingredient.unitId}
					lastCell={segmentLengths.includes(ingredient.index + 1)}
					disabled={!arePermissionsInUserPermissions([PERMISSIONS.READUNIT])}
				/>
			),
		},
	}));

	const columns = createDefaultColumns(t);

	return (
		<>
			<div style={{ padding: '1rem' }} />
			{element}
			{isDesktop ? (
				<AirTable
					columns={columns}
					data={tableData}
					dividers={segmentLengths}
					readOnly={props.readOnly}
				>
					{props.segments.map((x, i) => (
						<div
							className="segment-image-colspan"
							key={i}
							style={{ width: '100%', height: '100%' }}
						>
							<HighlightingSegment key={i} segment={x} />
						</div>
					))}
				</AirTable>
			) : (
				<>
					{props.segments.map((x, i) => (
						<div
							className="segment-image-colspan"
							key={i}
							style={{ width: '100%', height: '100%' }}
						>
							<HighlightingSegment key={i} segment={x} />
						</div>
					))}
					<div style={{ marginTop: '1rem' }}>
						<AirTable
							columns={columns}
							data={tableData}
							dividers={segmentLengths}
							readOnly={props.readOnly}
						/>
					</div>
				</>
			)}
		</>
	);
};

export default WithSelectedCulture(DefaultView);
